import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import { clearAuth, getAuthToken } from "../auth/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://pelaj-forever2025-production.up.railway.app/weddingAccess";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = useMemo(() => getAuthToken(), []);

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [totalGuests, setTotalGuests] = useState(0);
  const [checkedInGuests, setCheckedInGuests] = useState(0);

  // Guests table state
  const [showGuests, setShowGuests] = useState(false);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [guestsError, setGuestsError] = useState("");
  const [guests, setGuests] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const logout = () => {
    clearAuth();
    navigate("/admin/login", { replace: true });
  };

  // If no token, go login
  useEffect(() => {
    if (!token) navigate("/admin/login", { replace: true });
  }, [token, navigate]);

  const fetchStats = async () => {
    setStatsError("");
    setStatsLoading(true);
  
    try {
      const res = await fetch(`${API_BASE_URL}/countGuests`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
  
      const json = await res.json();
  
      if (!res.ok || json?.statusCode !== 200) {
        throw new Error(json?.description || "Failed to load stats");
      }
  
      setTotalGuests(Number(json?.data?.totalGuests || 0));
      setCheckedInGuests(Number(json?.data?.checkedIn || 0));
    } catch (e) {
      setStatsError(e?.message || "Failed to load dashboard stats");
    } finally {
      setStatsLoading(false);
    }
  };  

//   // ---- RECOMMENDED: hit a stats endpoint (fast + accurate)
//   const fetchStats = async () => {
//     setStatsError("");
//     setStatsLoading(true);

//     try {
//       // ✅ Recommended backend endpoint (you’ll add it below):
//       const res = await fetch(`${API_BASE_URL}/admin/stats`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       // If you haven’t added it yet, it will 404.
//       if (res.ok) {
//         const json = await res.json();
//         // Expected: { totalGuests: 123, checkedInGuests: 45 }
//         setTotalGuests(json.totalGuests || 0);
//         setCheckedInGuests(json.checkedInGuests || 0);
//         return;
//       }

//       // ---- FALLBACK (works with your existing getAllGuests):
//       // Trick: request size=1 => totalPages becomes totalGuests (because totalPages = ceil(total/1))
//       const fallbackRes = await fetch(
//         `${API_BASE_URL}/getAllGuests?page=0&size=1`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       const fallbackJson = await fallbackRes.json();

//       if (!fallbackRes.ok || fallbackJson?.statusCode !== 200) {
//         throw new Error(fallbackJson?.description || "Failed to load stats");
//       }

//       const data = fallbackJson?.data;
//       const total = Number(data?.totalPages || 0); // size=1 => totalPages == totalGuests
//       setTotalGuests(total);

//       // Checked-in count accurately needs backend support.
//       // We'll show "0" until /admin/stats exists.
//       setCheckedInGuests(0);
//     } catch (e) {
//       setStatsError(e?.message || "Failed to load dashboard stats");
//     } finally {
//       setStatsLoading(false);
//     }
//   };

  const fetchGuestsPage = async (p) => {
    setGuestsError("");
    setGuestsLoading(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/getAllGuests?page=${p}&size=${size}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();

      if (!res.ok || json?.statusCode !== 200) {
        throw new Error(json?.description || "Failed to load guests");
      }

      const data = json?.data;
      setGuests(Array.isArray(data?.content) ? data.content : []);
      setTotalPages(Number(data?.totalPages || 0));
      setPage(Number(data?.page || p));
    } catch (e) {
      setGuestsError(e?.message || "Unable to load guests");
    } finally {
      setGuestsLoading(false);
    }
  };

  const goToPage = (p) => {
    setPage(p);
    fetchGuestsPage(p);
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showGuests) fetchGuestsPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGuests]);

  const openGuests = () => {
    setShowGuests((v) => {
      const next = !v;
      if (!v) {
        // opening: reset to first page
        setPage(0);
        setGuests([]);
        setTotalPages(0);
        // fetch page 0
        setTimeout(() => fetchGuestsPage(0), 0);
      }
      return next;
    });
  };

  return (
    <SiteLayout>
      <div className="admin-page">
        <div className="admin-card">
          {/* Header Row */}
          <div className="admin-row" style={{ alignItems: "center" }}>
            <div>
              <h2 className="admin-title" style={{ marginBottom: 4 }}>
                Admin Dashboard
              </h2>
              <p className="admin-subtitle" style={{ margin: 0 }}>
                Overview & quick actions
              </p>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button className="admin-btn outline" onClick={fetchStats} disabled={statsLoading}>
                Refresh
              </button>
              <button className="admin-btn outline" onClick={logout}>
                Logout
              </button>
            </div>
          </div>

          {statsError && <div className="admin-error">{statsError}</div>}

          {/* Cards */}
          <div className="dash-grid">
            <button
              className="dash-card"
              onClick={() => navigate("/admin/checkin")}
              type="button"
            >
              <div className="dash-card-title">Check In</div>
              <div className="dash-card-sub">Scan guest QR codes</div>
            </button>

            <div className="dash-card" role="group" aria-label="Total guests">
              <div className="dash-card-title">Total Guests</div>
              <div className="dash-card-metric">
                {statsLoading ? "..." : totalGuests}
              </div>
              <div className="dash-card-sub">Invited guests</div>
            </div>

            <div className="dash-card" role="group" aria-label="Checked in guests">
              <div className="dash-card-title">Checked In</div>
              <div className="dash-card-metric">
                {statsLoading ? "..." : checkedInGuests}
              </div>
              <div className="dash-card-sub">
                Guest already checked in
              </div>
            </div>

            <button className="dash-card" onClick={() => navigate("/admin/guests")} type="button">
              <div className="dash-card-title">Get All Guests</div>
              <div className="dash-card-sub">
                View the guest list
              </div>
            </button>
          </div>

          {/* Guests table */}
          {showGuests && (
            <div className="dash-panel">
              <div className="dash-panel-head">
                <div className="dash-panel-title">Guest List</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="admin-btn outline"
                    onClick={() => fetchGuestsPage(page)}
                    disabled={guestsLoading}
                  >
                    Refresh List
                  </button>
                  <button className="admin-btn outline" onClick={openGuests}>
                    Close
                  </button>
                </div>
              </div>

              {guestsError && <div className="admin-error">{guestsError}</div>}

              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Checked In</th>
                      <th>Band Code</th>
                      <th>QR Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestsLoading ? (
                      <tr>
                        <td colSpan={5} style={{ opacity: 0.8 }}>
                          Loading...
                        </td>
                      </tr>
                    ) : guests.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ opacity: 0.8 }}>
                          No guests found.
                        </td>
                      </tr>
                    ) : (
                      guests.map((g) => (
                        <tr key={g.id}>
                          <td>{g.name}</td>
                          <td>{g.phoneNumber}</td>
                          <td>{g.hasCheckedIn ? "Yes" : "No"}</td>
                          <td>{g.partyBandCode || "-"}</td>
                          <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                            {g.qrCode}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="dash-pagination">
                <button
                  className="admin-btn outline"
                  onClick={() => fetchGuestsPage(Math.max(0, page - 1))}
                  disabled={guestsLoading || page <= 0}
                >
                  Prev
                </button>

                <div className="dash-pagination-meta">
                  Page <b>{page + 1}</b> of <b>{totalPages || 1}</b>
                </div>

                <button
                  className="admin-btn outline"
                  onClick={() => fetchGuestsPage(Math.min(totalPages - 1, page + 1))}
                  disabled={guestsLoading || totalPages === 0 || page >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}
